// Govern (Brief 4 / P3) — the standing rules across every session. "The standing
// rules are the real control": set the STANDING DEFAULT once and every session
// follows it (session ▸ project ▸ global resolution on the server), then give a
// single session its own rule only when it needs an exception. Not "twelve
// dials" — one default + overrides. Token-pure; built on the real presence
// transport + the real POST/GET /rules engine.

import { usePresenceContext } from "@waytide/ui";
import { useProjectsContext } from "../dashboard/projects/ProjectsContext";
import { AuthorityDial } from "./AuthorityDial";
import { sessionLabel } from "@waytide/core";
import "./govern.css";

export function GovernPage() {
  const { sessions, loaded, error } = usePresenceContext();
  const { projects } = useProjectsContext();
  return (
    <div className="wt-govern">
      <header className="wt-govern__header">
        <h1 className="wt-govern__title">Govern</h1>
        <p className="wt-govern__subtitle">
          The standing rules across your sessions. Set the default once, refine
          it per project, and override a single session only when it needs one —
          most-specific wins (session ▸ project ▸ global).
        </p>
      </header>

      {/* Set policy ONCE: the standing default cascades to every session that
          has no rule of its own (UC-6 — set policy once, not per action). */}
      <section className="wt-govern__default" aria-label="Standing default">
        <span className="wt-govern__session">Standing default</span>
        <AuthorityDial scope={{ kind: "global" }} />
      </section>

      {/* Per-project: a project's rule governs every session in it that has no
          rule of its own — the tier between the standing default and a single
          session (Brief 4 / P8). */}
      {projects.length > 0 ? (
        <>
          <h2 className="wt-govern__roster-title">Per-project</h2>
          <ul className="wt-govern__list" data-testid="govern-projects">
            {projects.map((p) => (
              <li key={p.project_id} className="wt-govern__row">
                <span className="wt-govern__session">{p.name}</span>
                <AuthorityDial
                  scope={{
                    kind: "project",
                    projectKey: String(p.project_id),
                    projectName: p.name,
                  }}
                />
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {error ? (
        <p className="wt-govern__empty">Couldn't reach your sessions: {error}</p>
      ) : !loaded ? (
        <p className="wt-govern__empty">Reading your sessions…</p>
      ) : sessions.length === 0 ? (
        <p className="wt-govern__empty">
          No active sessions — the standing default governs new ones as they
          start.
        </p>
      ) : (
        <>
          <h2 className="wt-govern__roster-title">Per-session overrides</h2>
          <ul className="wt-govern__list" data-testid="govern-list">
            {sessions.map((s) => (
              <li key={s.thread_id} className="wt-govern__row">
                <span
                  className="wt-govern__session"
                  title={`Session #${s.thread_id}`}
                >
                  {sessionLabel(s)}
                </span>
                <AuthorityDial
                  scope={{ kind: "session", threadId: s.thread_id }}
                />
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
